/** Width the ring adds around the portrait (3px ring + 2px gap, both sides). */
export const STORY_RING_EXTRA = 10;

const RING_GRADIENT =
  'conic-gradient(from 210deg, #D41920, #E84B5A, #F2A0C4, #F8D7DD, #D41920)';

/**
 * Styles for the ring button: a lift + glow on hover/focus so it reads as a
 * button, and a slow breathing glow while her story is still unseen. Include
 * this string once in the page's injected <style> block.
 */
export const STORY_RING_STYLES = `
@keyframes storyRingBreathe {
  0%, 100% { box-shadow: 0 0 0 0 rgba(212, 25, 32, 0); }
  50% { box-shadow: 0 0 0 6px rgba(212, 25, 32, 0.22); }
}
.story-ring-btn { transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease; }
.story-ring-btn[data-seen="false"] { animation: storyRingBreathe 2.6s ease-in-out infinite; }
.story-ring-btn:hover, .story-ring-btn:focus-visible {
  transform: scale(1.06);
  animation: none;
  box-shadow: 0 6px 18px rgba(212, 25, 32, 0.35);
  filter: brightness(1.04);
}
.story-ring-btn:active { transform: scale(0.98); }
.story-ring-btn:hover .story-ring-badge, .story-ring-btn:focus-visible .story-ring-badge { transform: scale(1.12); }
.story-ring-btn:focus-visible { outline: 3px solid #F2A0C4; outline-offset: 3px; }
.story-ring-badge { transition: transform 0.18s ease; }
@media (prefers-reduced-motion: reduce) {
  .story-ring-btn[data-seen="false"] { animation: none; }
  .story-ring-btn:hover, .story-ring-btn:focus-visible, .story-ring-btn:active { transform: none; }
  .story-ring-btn:hover .story-ring-badge, .story-ring-btn:focus-visible .story-ring-badge { transform: none; }
}
`;

/**
 * StoryRing — the familiar "there's a story here" ring around her portrait,
 * as a real button that opens the story. Coloured while the story is unseen,
 * a quiet grey ring once it was seen. A small "play" badge sits on the ring's
 * corner so the portrait reads as something to tap, not just a picture.
 *
 * Wrap a portrait that is `STORY_RING_EXTRA` px smaller than the space it used
 * to take, so the poster layout does not move.
 */
export function StoryRing({
  seen,
  label,
  gapColor,
  seenColor,
  compact = false,
  onOpen,
  children,
}: {
  seen: boolean;
  /** accessible name, e.g. "Open Nise's story" */
  label: string;
  /** the poster colour, showing between ring and portrait */
  gapColor: string;
  /** ring colour once seen (a muted tone of the poster's ink) */
  seenColor: string;
  /** smaller badge, for the small portrait of an expanded poster */
  compact?: boolean;
  onOpen: () => void;
  children: React.ReactNode;
}) {
  const badge = compact ? 18 : 24;
  return (
    <button
      type="button"
      className="story-ring-btn"
      aria-label={label}
      title={label}
      data-testid="story-ring"
      data-seen={seen ? 'true' : 'false'}
      onClick={(e) => {
        // The poster behind toggles open/closed on click — not for this tap.
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        position: 'relative',
        border: 0,
        margin: 0,
        padding: seen ? 2 : 3,
        borderRadius: '50%',
        background: seen ? seenColor : RING_GRADIENT,
        display: 'inline-grid',
        placeItems: 'center',
        flex: 'none',
        minWidth: 44,
        minHeight: 44,
        cursor: 'pointer',
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          borderRadius: '50%',
          padding: seen ? 3 : 2,
          background: gapColor,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {children}
      </span>

      {/* "Play" badge: the tap cue, in the story red with the poster colour as
          its rim so it sits on the ring like a sticker */}
      <span
        aria-hidden
        className="story-ring-badge"
        data-testid="story-ring-badge"
        style={{
          position: 'absolute',
          right: compact ? -3 : -4,
          bottom: compact ? -3 : -4,
          width: badge,
          height: badge,
          borderRadius: '50%',
          background: seen ? seenColor : '#D41920',
          border: `2px solid ${gapColor}`,
          boxSizing: 'border-box',
          display: 'grid',
          placeItems: 'center',
          boxShadow: '0 2px 6px rgba(26, 16, 24, 0.3)',
        }}
      >
        <svg
          width={compact ? 7 : 9}
          height={compact ? 8 : 10}
          viewBox="0 0 9 10"
          fill="#FFFFFF"
          style={{ display: 'block', marginLeft: 1 }}
        >
          <path d="M0 0 L9 5 L0 10 Z" />
        </svg>
      </span>
    </button>
  );
}
